import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Container} from 'reactstrap';
import styled from 'styled-components';
import {MapContext} from '../../apiContexts';
import {useMap} from '../../components/map/hooks';
import useMapReport from '../../components/map/hooks/useMapReport';
import {goToReportRow, startsNewReport} from '../../helpers/report';
import history from '../../routes/history';
import FloatingReport from '../../views/reports/floating-report';
import MapElement from '../../components/map/MapElement';
import useMapLabel from '../../components/map/hooks/useMapLabel';
import useMapGeofences from '../../components/map/hooks/useMapGeofences';
import useMapTrace from '../../components/map/hooks/useMapTrace';
import {useIntl} from 'react-intl';
import useStore from 'src/hooks/use-store.js';
import useMapDevice from 'src/components/map/hooks/useMapDevice.js';
import useMapPosition from 'src/components/map/hooks/useMapPosition.js';
import {selectedRowCoordinate} from 'src/helpers/report';

const Map = styled.div`
    height: 100%;
    width: 100%;

    .map-element {
        height: 100%;
        width: 100%;
        position: relative;
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
        border-radius: 12px;

        .ol-viewport {
            border-radius: 12px;
        }
    }
`;

const AdminReportContainer = ({location, ...props}) => {
    const mapRef = useRef();
    const intl = useIntl();
    const {store} = useStore();
    const [report, setReport] = useState();

    const query = useMemo(
        () => ({search: location.search, hash: location.hash}),
        [location.search],
    );

    useEffect(() => {
        if (query.search) {
            setReport(query.hash.slice(1));
        } else {
            setReport(null);
        }
    }, [query]);

    // Fechar o relatorio (botao ou navegacao) tem que devolver o mapa ao normal -- sem isso
    // MapContext.data ficava com o ultimo relatorio pra sempre, e dispositivo/label/trilha
    // continuavam escondidos mesmo depois de fechar.
    useEffect(() => {
        if (!report) {
            setData([]);
        }
    }, [report]);

    const {
        instance,
        setReportData,
        trace,
        setTrace,
        label,
        toggleLabel,
        selectedMap,
        setSelectedMap,
        showDevices,
        setShowDevices: toggleDevice,
        replaying,
        replaySpeed,
        playReplay,
        pauseReplay,
        setReplaySpeed,
        replayPositionId,
    } = useMap(
        {
            target: mapRef,
            center: store.map.olCenter,
            zoom: store.map.zoom,
            tiers: store.map.olCustomTiles,
            initAllDevices: false, // INFO: devices will only be visible if map view was previously opened
        },
        useMapDevice,
        useMapPosition,
        useMapReport,
        useMapLabel,
        useMapGeofences,
        useMapTrace,
    );

    // REPORTS
    const lastRowZoom = useRef(null);
    const {
        selectedItem,
        data,
        setData,
        setSelectedRow,
        selectedRow,
        setProps,
    } = useContext(MapContext);

    useEffect(() => {
        if (instance && selectedRow && selectedRow !== undefined) {
            // Guarda tambem de quem e a linha escolhida: e o que traz o traco daquele veiculo para
            // a frente quando varios percorrem a mesma rota.
            instance.setSelectedReportPoint(selectedRow.id, selectedRow.deviceId, selectedRowCoordinate(selectedRow));
        }
    }, [selectedRow, instance]);

    useEffect(() => {
        if (instance && selectedItem) {
            const {longitude, latitude} = selectedItem;

            if (longitude && latitude) {
                goToReportRow(instance, [longitude, latitude], lastRowZoom);
            } else if (data?.type !== 'journey_trip' && data?.type !== 'trip') {
                // journey_trip/trip already framed by fitReportExtent() in useMapReport
                instance.goTo(null, instance._instance.getView().getZoom() - 0.0001);
            }
        }
    }, [instance, selectedItem, data]);

    useEffect(() => {
        if (startsNewReport(data)) lastRowZoom.current = null; // a primeira linha de um relatorio novo usa o zoom do relatorio
        setReportData(data);
    }, [data]);

    const close = () => {
        history.push('/admin');
        instance.removeReports();
    };

    useEffect(() => {
        if (instance) {
            setProps({
                map: instance,
                replay: {replaying, replaySpeed, playReplay, pauseReplay, setReplaySpeed, replayPositionId},
            });
        }
    }, [instance, replaying, replaySpeed, playReplay, pauseReplay, setReplaySpeed, replayPositionId]);

    useEffect(() => {
        if (instance) {
            const map = instance.instance;
            instance.setI18n(intl);
            map.on('click', (event) => {
                const [feature, layer] =
                map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
                    return [feature, layer];
                }) || [];

                if (!layer) return;

                const layerName = layer.get('name');
                if (layerName === 'reportLayer') {
                    const featureName = feature.get('name');
                    if (featureName !== 'line') {
                        setSelectedRow({
                            id: feature.get('id') || feature.getId(),
                            scroll: true,
                        });
                    }
                }
            });

            map.on('pointermove', (event) => {
                map.getTargetElement().style.cursor = map.hasFeatureAtPixel(event.pixel) ?
                    'pointer' :
                    '';
            });
        }
    }, [instance]);

    return (
        <Container fluid className="px-4 pb-4 position-relative h-100">
            <Map>
                <MapElement
                    mapRef={mapRef}
                    className="custom-shadow-2"
                    {...{
                        trace,
                        setTrace,
                        label,
                        toggleLabel,
                        selectedMap,
                        setSelectedMap,
                        showDevices,
                        toggleDevice,
                    }}
                />
            </Map>
            {report && (
                <FloatingReport type={report} location={location} close={close}/>
            )}
        </Container>
    );
};

export default AdminReportContainer;
